//
// Copyright (c) 2023 Emplifi
//

import { PLACEHOLDER_VALUE } from "../ckEditorConstants";
import type {
  DowncastConversionApi,
  DowncastDispatcher,
  Editor,
  ImageUtils,
  Matcher,
  ModelElement,
  ModelItem,
  UpcastConversionApi,
  UpcastConversionData,
  UpcastDispatcher,
  ViewElement,
  ViewNode,
} from "ckeditor5";

export const ATTRIBUTE_CONTENT_MAPPING = "linkContentMapping";
export const ATTRIBUTE_LINK_TOPIC = "linkTopic";
export const ATTRIBUTE_LINK_TYPE = "linkType";

export const LINK_TYPE = {
  CONTENT_MAPPING: "contentMapping",
  EMAIL: "email",
  PHONE: "phone",
  TOPIC_LINK: "topicLink",
  URL: "url",
};

export function buildTopicUrl(kbId: string | number, topicJsonOrId: unknown): string {
  if (topicJsonOrId && topicJsonOrId != PLACEHOLDER_VALUE) {
    let topicId: string | number | undefined;
    if (typeof topicJsonOrId == "number") {
      topicId = topicJsonOrId;
    } else {
      let topicObject: { id: string } | undefined;
      if (typeof topicJsonOrId == "string") {
        topicObject = JSON.parse(topicJsonOrId);
      } else if (typeof topicJsonOrId == "object") {
        topicObject = topicJsonOrId as { id: string };
      }
      if (topicObject) {
        topicId = topicObject.id;
      }
    }
    if (topicId && topicId != PLACEHOLDER_VALUE) {
      return `#${kbId}/topic/${topicId}`;
    }
  }
  return "";
}

export interface ContentMappingModelAttributes {
  id: string;
  name?: string;
}

export interface ContentMappingViewAttributes {
  "data-rdcontent": string;
  "data-rdcontent-name"?: string;
}

export function contentMappingModelAttributeToViewAttributes(
  modelAttributeValue: string,
): ContentMappingViewAttributes | null {
  if (!modelAttributeValue || modelAttributeValue == PLACEHOLDER_VALUE) {
    return null;
  }
  const contentMapping = JSON.parse(modelAttributeValue);
  const viewAttributes: ContentMappingViewAttributes = {
    "data-rdcontent": contentMapping.id,
  };
  if (contentMapping.name && contentMapping.name != PLACEHOLDER_VALUE) {
    viewAttributes["data-rdcontent-name"] = contentMapping.name;
  }
  return viewAttributes;
}

export function contentMappingViewElementToModelAttribute(
  _editor: Editor,
  viewElement?: ViewElement,
): string | null {
  if (!viewElement) {
    return null;
  }
  const contentMappingId = viewElement.getAttribute("data-rdcontent");
  if (!contentMappingId || contentMappingId == PLACEHOLDER_VALUE) {
    return null;
  }
  const contentMapping: ContentMappingModelAttributes = {
    id: contentMappingId,
  };
  const contentMappingName = viewElement.getAttribute("data-rdcontent-name");
  if (contentMappingName && contentMappingName != PLACEHOLDER_VALUE) {
    contentMapping.name = contentMappingName;
  }
  return JSON.stringify(contentMapping);
}

/* eslint-disable no-unused-vars */
export type ViewAttributeConverter = (modelAttributeValue?: string) => unknown | null | undefined;
export type ViewElementConverter = (
  editor: Editor,
  viewElement?: ViewElement,
) => unknown | null | undefined;
/* eslint-enable no-unused-vars */

/**
 * A helper function that will determine if an image contains the attribute for a
 * particular extra feature (e.g., Topic Link).  It will help in setting the extra attributes
 * for that feature on the `<a>` tag.
 */
export function downcastImageLinkDataAttributes(
  modelAttributeName: string,
  viewAttributeConverter: ViewAttributeConverter,
) {
  // Adapted from downcastImageLinkManualDecorator in ImageLinkEditing
  return (dispatcher: DowncastDispatcher) => {
    dispatcher.on(
      `attribute:${modelAttributeName}:imageBlock`,
      (
        _evt: unknown,
        data: { item: ModelElement; attributeNewValue: string },
        conversionApi: DowncastConversionApi,
      ) => {
        const viewFigure: ViewElement | undefined = conversionApi.mapper.toViewElement(data.item);
        if (!viewFigure) {
          return;
        }
        const linkInImageNode: ViewNode | undefined = Array.from(viewFigure.getChildren()).find(
          (child) => child.is("element", "a"),
        );
        // The <a> element was removed by the time this converter is executed.
        // It may happen when the base `linkHref` and decorator attributes are removed
        // at the same time (see #8401).
        if (!linkInImageNode) {
          return;
        }
        const linkInImageElement = linkInImageNode as ViewElement;
        const viewAttributes = viewAttributeConverter(data.attributeNewValue);
        if (!viewAttributes) {
          return;
        }
        for (const [key, value] of Object.entries(viewAttributes)) {
          if (value && value !== PLACEHOLDER_VALUE) {
            conversionApi.writer.setAttribute(key, value, linkInImageElement);
          } else {
            conversionApi.writer.removeAttribute(key, linkInImageElement);
          }
        }
      },
    );
  };
}

export interface TopicLinkViewAttributes {
  "data-intent": string;
  "data-intent-label": string;
}

export function topicLinkModelAttributeToViewAttributes(
  modelAttributeValue?: string,
): TopicLinkViewAttributes | null {
  if (!modelAttributeValue || modelAttributeValue == PLACEHOLDER_VALUE) {
    return null;
  }
  const topic = JSON.parse(modelAttributeValue);
  if (
    !topic.id ||
    topic.id == PLACEHOLDER_VALUE ||
    !topic.label ||
    topic.label == PLACEHOLDER_VALUE
  ) {
    return null;
  }
  return {
    "data-intent": topic.id,
    "data-intent-label": topic.label,
  };
}

export function topicLinkViewElementToModelAttribute(
  _editor: Editor,
  viewElement?: ViewElement,
): string | null {
  if (!viewElement) {
    return null;
  }
  const topicId = viewElement.getAttribute("data-intent");
  if (!topicId || topicId == PLACEHOLDER_VALUE) {
    return null;
  }
  let topicLabel: string | undefined = viewElement.getAttribute("data-intent-label");
  return JSON.stringify({
    id: topicId,
    label: topicLabel,
  });
}

/**
 * A helper function that will determine if a link element is for an image and contains the
 * attribute for a particular extra feature (e.g., Topic Link).  It will help in setting the extra
 * attributes for that feature on the image model
 */
export function upcastImageLinkDataAttributes(
  editor: Editor,
  matcher: Matcher,
  modelAttributeName: string,
  viewElementConverter: ViewElementConverter,
) {
  // Adapted from updateImageLinkManualDecorator in LinkImageEditing
  const isImageInlinePluginLoaded = editor.plugins.has("ImageInlineEditing");
  const imageUtils: ImageUtils = editor.plugins.get("ImageUtils");
  return (dispatcher: UpcastDispatcher) => {
    dispatcher.on(
      "element:a",
      (_evt: unknown, data: UpcastConversionData, conversionApi: UpcastConversionApi) => {
        const viewLink = data.viewItem as ViewElement;
        const imageInLink = imageUtils.findViewImgElement(viewLink);
        // We need to check whether an image is inside a link because the converter handles
        // only manual decorators for linked images. See #7975.
        if (!imageInLink) {
          return;
        }
        const blockImageView = imageInLink.findAncestor((element) =>
          imageUtils.isBlockImageView(element),
        );
        if (isImageInlinePluginLoaded && !blockImageView) {
          return;
        }
        const result = matcher.match(viewLink);
        // The link element does not have required attributes or/and proper values.
        if (!result) {
          return;
        }
        const modelAttributeValue = viewElementConverter(editor, viewLink);
        // Check whether we can consume those attributes.
        if (!conversionApi.consumable.consume(viewLink, result.match)) {
          return;
        }
        // At this stage we can assume that we have the `<imageBlock>` element.
        // `nodeBefore` comes after conversion: `<a><img></a>`.
        // `parent` comes with a full image definition: `<figure><a><img></a></figure>.
        // See the body of the `upcastLink()` function.
        const modelElement = (data.modelCursor.nodeBefore || data.modelCursor.parent) as ModelItem;
        if (modelAttributeValue && modelAttributeValue != PLACEHOLDER_VALUE) {
          conversionApi.writer.setAttribute(modelAttributeName, modelAttributeValue, modelElement);
        } else {
          conversionApi.writer.removeAttribute(modelAttributeName, modelElement);
        }
      },
      { priority: "high" },
    );
  };
}
